import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  Bell,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  LayoutList,
  LogOut,
  Menu as MenuIcon,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Users,
  UserPlus,
  Briefcase,
  X,
} from "lucide-react";
import { useUserContext } from "../Context/UserContext";
import { useLogout } from "../Utils/useLogout";
import { portalHome, usePortal } from "../Portal/PortalContext";
import { useUnreadUpdates } from "../Reviews/Notifications";
import "../Documents/Documents.css";
import "../Design/ui.css";

export default function Menu({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const { logout, signingOut } = useLogout();
  const { access } = usePortal();
  const unread = useUnreadUpdates(Boolean(user));
  const { pathname, search } = useLocation();
  const accountType = access?.accountType;
  const home = access ? portalHome(access) : "/";
  const portalLabel =
    accountType === "admin"
      ? "Admin portal"
      : accountType === "attorney"
        ? "Attorney portal"
        : "Customer portal";
  const [collapsed, setCollapsed] = useState(false);
  const [reviewNavExpanded, setReviewNavExpanded] = useState(false);
  const attorneyReview =
    accountType === "attorney" && /^\/reviews\/[^/]+\/?$/.test(pathname);
  const navigationCollapsed = attorneyReview ? !reviewNavExpanded : collapsed;
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLElement>(null);
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname, search]);
  useEffect(() => {
    if (!mobileOpen) return;
    const opener = toggle.current;
    close.current?.focus();
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
      if (e.key === "Tab") {
        const items = Array.from(
          sidebar.current?.querySelectorAll<HTMLElement>(
            "a[href],button:not([disabled])"
          ) || []
        ).filter(el => el.getClientRects().length > 0);
        const first = items[0],
          last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    const desktop = window.matchMedia("(min-width: 1001px)");
    const resize = () => {
      if (desktop.matches) setMobileOpen(false);
    };
    desktop.addEventListener("change", resize);
    window.addEventListener("keydown", escape);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", escape);
      desktop.removeEventListener("change", resize);
      opener?.focus();
    };
  }, [mobileOpen]);
  const name = user?.username || user?.email || "Your account";
  const initials = name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0])
    .join("")
    .toUpperCase();
  const section = pathname.startsWith("/admin")
    ? "Administration"
    : pathname.startsWith("/attorney")
      ? "Attorney workspace"
      : pathname.startsWith("/reviews")
        ? accountType === "customer"
          ? "Questions"
          : "Attorney reviews"
        : pathname.startsWith("/notifications")
          ? accountType === "customer"
            ? "Notifications"
            : "Updates"
          : pathname.startsWith("/account")
            ? "Account"
            : pathname.startsWith("/documents") ||
                pathname.startsWith("/workproducts")
              ? "Documents"
              : "Questions";
  const brand = (
    <Link className="rv-brand" to={home} aria-label="Reviewed home">
      <span className="rv-brand-mark">
        <Check size={20} strokeWidth={2.3} />
      </span>
      <span className="rv-brand-name">
        Reviewed<span>.</span>
      </span>
    </Link>
  );
  if (pathname === "/" && !user)
    return <div className="rv-app">{children}</div>;
  if (!user || !access)
    return (
      <div className="rv-app rv-public">
        <header className="rv-public-header">
          {brand}
          {user ? (
            <button
              className="btn btn-light"
              onClick={() => void logout()}
              disabled={signingOut}
            >
              Sign out
            </button>
          ) : (
            <Link to="/login">
              Sign in <ArrowUpRight size={14} />
            </Link>
          )}
        </header>
        {children}
      </div>
    );
  const nav = (
    to: string,
    label: string,
    Icon: any,
    end = false,
    active?: boolean
  ) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `rv-nav-link${(active ?? isActive) ? " active" : ""}`
      }
      aria-current={active === false ? false : "page"}
      title={navigationCollapsed ? label : undefined}
      aria-label={
        to === "/notifications" && unread > 0
          ? `${label} (${unread} unread)`
          : label
      }
    >
      <Icon size={18} strokeWidth={1.65} />
      <span>{label}</span>
      {to === "/notifications" && unread > 0 && (
        <b className="rv-unread">{unread > 99 ? "99+" : unread}</b>
      )}
    </NavLink>
  );
  return (
    <div
      className={`rv-app rv-shell${navigationCollapsed ? " is-collapsed" : ""}${mobileOpen ? " mobile-open" : ""}`}
    >
      <a className="rv-skip" href="#workspace-content">
        Skip to content
      </a>
      {mobileOpen && (
        <button
          className="rv-nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        ref={sidebar}
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen ? true : undefined}
        className="rv-sidebar"
        id="workspace-navigation"
        aria-label="Workspace navigation"
      >
        <div className="rv-sidebar-brand">
          {brand}
          <button
            ref={close}
            className="rv-mobile-close rv-icon-button"
            aria-label="Close navigation"
            onClick={() => {
              setMobileOpen(false);
            }}
          >
            <X size={18} />
          </button>
        </div>
        {accountType === "customer" && (
          <Link
            className="rv-new-question"
            to="/questions/new"
            title={navigationCollapsed ? "Ask a question" : undefined}
          >
            <Plus size={17} />
            <span>Ask a question</span>
          </Link>
        )}
        <nav aria-label="Main navigation">
          {accountType === "customer" && (
            <>
              {nav(
                "/questions",
                "My questions",
                MessageSquare,
                true,
                (pathname.startsWith("/questions") &&
                  pathname !== "/questions/new") ||
                  pathname.startsWith("/reviews")
              )}
            </>
          )}
          {accountType === "attorney" && (
            <>
              <p className="rv-nav-label">Your practice</p>
              {access.attorney && (
                <>
                  {nav(
                    "/attorney/reviews",
                    "Reviews",
                    BriefcaseBusiness,
                    true,
                    pathname === "/attorney/reviews" ||
                      pathname.startsWith("/reviews/")
                  )}
                </>
              )}
              {nav("/attorney/profile", "Professional profile", Users)}
            </>
          )}
          {accountType === "admin" && (
            <>
              <p className="rv-nav-label">Administration</p>
              {nav("/admin/reviews", "Assignments", LayoutList)}
              {nav("/admin/accounts", "Account requests", UserPlus)}
              {nav("/admin/applications", "Attorney applications", Briefcase)}
              {nav("/admin/attorneys", "Attorneys", Users)}
            </>
          )}
          {nav(
            "/notifications",
            accountType === "customer" ? "Notifications" : "Updates",
            Bell
          )}
        </nav>
        <div className="rv-sidebar-bottom">
          {accountType === "customer" ? (
            <Link
              className={`rv-account${pathname === "/account" ? " active" : ""}`}
              to="/account"
              title="Account"
            >
              <span className="rv-avatar">{initials}</span>
              <div>
                <strong title={name}>{name}</strong>
                <span>
                  {access?.targetedBeta ? "Account" : "Account and saved cards"}
                </span>
              </div>
            </Link>
          ) : (
            <div className="rv-account">
              <span className="rv-avatar">{initials}</span>
              <div>
                <strong title={name}>{name}</strong>
                <span>
                  {accountType === "admin"
                    ? "Administrator"
                    : accountType === "attorney"
                      ? access.attorney
                        ? "Attorney account"
                        : `Attorney · ${access.profile?.status || "pending"}`
                      : "Customer account"}
                </span>
              </div>
            </div>
          )}
          <div className="rv-account-actions">
            <button
              onClick={() => void logout()}
              disabled={signingOut}
              title="Sign out"
            >
              <LogOut size={16} />
              <span>{signingOut ? "Signing out…" : "Sign out"}</span>
            </button>
            <button
              className="rv-collapse"
              onClick={() =>
                attorneyReview
                  ? setReviewNavExpanded(!reviewNavExpanded)
                  : setCollapsed(!collapsed)
              }
              aria-label={
                navigationCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
            >
              {navigationCollapsed ? (
                <PanelLeftOpen size={17} />
              ) : (
                <PanelLeftClose size={17} />
              )}
            </button>
          </div>
        </div>
      </aside>
      <div className="rv-workspace" aria-hidden={mobileOpen ? true : undefined}>
        <header className="rv-topbar">
          <div>
            <button
              ref={toggle}
              className="rv-mobile-toggle rv-icon-button"
              aria-label="Open navigation"
              aria-expanded={mobileOpen}
              aria-controls="workspace-navigation"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon size={19} />
            </button>
            <span className="rv-topbar-home">Reviewed</span>
            <ChevronRight size={13} />
            <span>{section}</span>
          </div>
          {accountType !== "customer" && (
            <span className="rv-topbar-context">{portalLabel}</span>
          )}
        </header>
        <div id="workspace-content" className="rv-content" tabIndex={-1}>
          {children}
        </div>
      </div>
    </div>
  );
}
